// Fix mode: a body bolted to the world where it stands.
//
// Pressed, the button arms the canvas; a click then welds whatever link or
// object is under the pointer, and a click on something already welded lets it
// go again. Pressed again it disarms -- but nothing is released by that, because
// a fix is a fact about the scene rather than a property of the mode that made
// it. Hovering the button says what is currently held, and offers to let all of
// it go at once.
//
// It is what a bench is: a robot arm imported from a URDF has a floating root,
// so its own servos' reaction torques push it around the floor and the first
// joint to move takes the whole robot with it. Fixing the base link is the
// answer, and until now there was none -- `nudge.ts` is a *force*, so it can
// only ever hold a body approximately, and the engine's own way of pinning a
// body is a joint chosen when the robot is built, which is far too early: which
// link belongs on the bench is something the user decides after watching it
// fall over.
//
// **The weld is the engine's**, `Articulated::fix_to_world`, and it is a
// constraint rather than a swapped-in fixed joint: the body's own spatial
// velocity is held at zero, so it cannot leave the pose it was fixed in, and the
// articulated keeps the shape it had -- nothing keyed by joint index, which is
// every servo and every mesh, has to be rebuilt when a body is fixed or
// released.
//
// **Nothing is kept here but the mode.** What is fixed is read back out of the
// `Hybrid` whenever it is asked for, so a robot replaced under the panel cannot
// leave a stale name in the list, and a fix rides on the `Articulated` it
// belongs to rather than on an index that `removeOtherRobots` renumbers.

import { InterfaceHybrid } from "simulator";
import { Simulator } from "gorilla-physics-ui";
import { pick } from "./pick";

// How far the pointer may travel between press and release and still count as a
// click on a body. Anything further is an orbit: unlike nudging, this takes
// nothing away from the controls, since a fix is one click and there is no drag
// to keep the canvas for.
const CLICK_SLOP = 4; // px

let simulator: Simulator | undefined;
let hybrid: InterfaceHybrid | undefined;
let armed = false;
let allowed = true;
// What to switch off when this arms. The page wires it, rather than the two
// modes importing each other.
let disarmOthers: () => void = () => {};
// Where the button went down, while it is down. Undefined between presses, and
// also once a press has been ruled out as a click.
let pressedAt: { x: number; y: number } | undefined;

function element<T extends HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (!found) {
    throw new Error(`index.html has no #${id}`);
  }
  return found as T;
}

function canvas(): HTMLCanvasElement | undefined {
  return simulator?.graphics.renderer.domElement;
}

// Arm the mode's toggle and put its listeners on the canvas. Called once, with
// the scene the whole page shares -- as `allowNudging` is, and for the same
// reason: a drop replaces what is in that scene but never the handle to it.
export function allowFixing(
  scene: Simulator,
  world: InterfaceHybrid,
  disarmOtherModes: () => void,
) {
  simulator = scene;
  hybrid = world;
  disarmOthers = disarmOtherModes;

  const toggle = element<HTMLButtonElement>("fix-toggle");
  toggle.addEventListener("click", () => setArmed(!armed));
  // Showing the list is CSS's, off the column's `:hover` so that it does not go
  // again as the pointer moves down onto it. Filling it is this, on reaching
  // the button -- which is the only way to the list, and what makes what is in
  // it an answer about now rather than about the last drop.
  toggle.addEventListener("pointerenter", showFixed);
  toggle.addEventListener("focus", showFixed);

  const element_ = canvas();
  if (!element_) {
    return;
  }
  element_.addEventListener("pointerdown", onPointerDown);
  element_.addEventListener("pointerup", onPointerUp);
  element_.addEventListener("pointercancel", () => (pressedAt = undefined));
}

// Let the page refuse the mode while something else owns the pointer -- placing
// a dropped object, whose gizmo is on the same canvas. Refused, the mode is
// switched off rather than merely blocked, so the button never says it is on
// while it is not.
export function setFixingAllowed(value: boolean) {
  allowed = value;
  if (!allowed) {
    setArmed(false);
  }
  element<HTMLButtonElement>("fix-toggle").disabled = !allowed;
}

// Switch the mode off from outside -- `nudge.ts` arming, which wants the same
// canvas. What is already fixed stays fixed.
export function disarmFixing() {
  setArmed(false);
}

function setArmed(value: boolean) {
  if (value && !allowed) {
    return;
  }
  // The two modes both answer a press on the canvas, and a press that did two
  // things at once would be neither.
  if (value) {
    disarmOthers();
  }
  armed = value;
  pressedAt = undefined;
  const toggle = element<HTMLButtonElement>("fix-toggle");
  toggle.setAttribute("aria-pressed", String(armed));
  toggle.classList.toggle("held", armed);
  const element_ = canvas();
  if (element_) {
    element_.style.cursor = armed ? "crosshair" : "";
  }
}

function onPointerDown(event: PointerEvent) {
  if (!armed || event.button !== 0) {
    return;
  }
  pressedAt = { x: event.clientX, y: event.clientY };
}

function onPointerUp(event: PointerEvent) {
  const down = pressedAt;
  pressedAt = undefined;
  if (!armed || !down || !simulator || !hybrid || event.button !== 0) {
    return;
  }
  const moved = Math.hypot(event.clientX - down.x, event.clientY - down.y);
  if (moved > CLICK_SLOP) {
    return; // the camera was being orbited, not a body picked
  }
  const picked = pick(simulator, hybrid, event);
  if (!picked) {
    return;
  }
  try {
    // Clicking what is already held is what releases it. roadmap#16 does not
    // say so, and the alternative is that a mis-click stands until the next
    // drop.
    if (hybrid.is_body_fixed_at(picked.articulated, picked.body)) {
      hybrid.release_body_at(picked.articulated, picked.body);
    } else {
      hybrid.fix_body_at(picked.articulated, picked.body);
    }
  } catch (error) {
    console.error("fix: could not change what is fixed", error);
    return;
  }
  showFixed();
}

// Let everything go at once, off the list's own button. The same sweep that
// fills the list, releasing instead of naming -- there is nothing kept here to
// clear, so what is released is whatever the `Hybrid` says is held when the
// button is pressed. The mode is left as it was: releasing every body is a fact
// about the scene, the way fixing one is, and not an answer about whether the
// next click should fix another.
function clearFixed() {
  if (!hybrid) {
    return;
  }
  try {
    for (let i = 0; i < hybrid.n_articulated(); i++) {
      for (let j = 0; j < hybrid.n_bodies_articulated(i); j++) {
        if (hybrid.is_body_fixed_at(i, j)) {
          hybrid.release_body_at(i, j);
        }
      }
    }
  } catch (error) {
    console.error("fix: could not release what is fixed", error);
  }
  showFixed();
}

// What is welded to the world, straight out of the `Hybrid` -- every body of
// every articulated, robot and dropped object alike, since nothing in the scene
// tells one from the other.
function showFixed() {
  const list = element<HTMLDivElement>("fix-list");
  list.replaceChildren();
  const names: string[] = [];
  if (hybrid) {
    try {
      for (let i = 0; i < hybrid.n_articulated(); i++) {
        for (let j = 0; j < hybrid.n_bodies_articulated(i); j++) {
          if (hybrid.is_body_fixed_at(i, j)) {
            names.push(hybrid.frame_articulated_body(i, j));
          }
        }
      }
    } catch (error) {
      console.error("fix: could not read what is fixed", error);
    }
  }
  if (names.length === 0) {
    const empty = document.createElement("div");
    empty.className = "fix-empty";
    empty.textContent = "Nothing is fixed";
    list.appendChild(empty);
    return;
  }
  // Only over a list that has something in it: with nothing fixed there is
  // nothing for it to do, and the row that says so is the whole answer.
  const clear = document.createElement("button");
  clear.type = "button";
  clear.className = "fix-clear";
  clear.textContent = "Clear All";
  clear.addEventListener("click", clearFixed);
  list.appendChild(clear);
  for (const name of names) {
    const row = document.createElement("div");
    row.className = "fix-row";
    row.textContent = name;
    list.appendChild(row);
  }
}
